/**
 * Çekiliş sayfası (/cekilis).
 *
 * Oyuncunun üç sorusu vardır ve sayfa onları bu sırayla yanıtlar: **ne
 * kazanabilirim** (ödül havuzu ve podyum), **nasıl katılırım** (her ₺X
 * yatırım = 1 bilet) ve **kaç biletim var** (bilet kutusu, ilerleme çubuğu,
 * numaralar). Birden çok çekiliş aynı anda yayında olabilir; üstteki şerit
 * aralarında geçirir ve tek çekilişte hiç çizilmez.
 *
 * Bölüm panelden kapatılabilir: kapalıyken uç `enabled: false` döner ve sayfa
 * "şu anda kapalı" kartını çizer — menüden düşen bağlantı yalnızca yolu
 * gizler, adresi elle giren de bir şey görmemelidir.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Ticket, Trophy, Gift, Wallet, Package, Coins, Sparkles,
  CheckCircle2, LogIn, Dices, CalendarClock, ShieldCheck, Users,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import client, { resolveApiAssetUrl } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import AuthModal from '../components/AuthModal';
import { ticketProgress } from '../lib/raffle';
import './Raffle.css';

const money = (value) => new Intl.NumberFormat('tr-TR', {
  style: 'currency', currency: 'TRY', maximumFractionDigits: 2,
}).format(Number(value || 0));

const compact = (value) => new Intl.NumberFormat('tr-TR').format(Number(value || 0));

const formatDate = (value) => (
  value ? new Date(value).toLocaleString('tr-TR', {
    day: '2-digit', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }) : ''
);

// Ekranda en çok bu kadar bilet numarası çizilir: yüzlerce bilet kazanan
// üyede liste sayfanın tamamını kaplıyordu; kalanı sayıyla söylenir.
const STUB_LIMIT = 40;

const STATUS_CHIPS = {
  active: { label: 'Bilet kazanılıyor', tone: 'is-live' },
  closed: { label: 'Kura bekleniyor', tone: '' },
  drawn: { label: 'Kura çekildi', tone: 'is-gold' },
};

const PRIZE_ICONS = { cash: Coins, bonus: Sparkles, custom: Package };

/** Geri sayımın parçaları; saniye de gösterilir çünkü kura anı bir olaydır. */
const splitRemaining = (target, now) => {
  const diff = new Date(target).getTime() - now;
  if (!target || Number.isNaN(diff) || diff <= 0) return null;
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff % 86400000) / 3600000),
    minutes: Math.floor((diff % 3600000) / 60000),
    seconds: Math.floor((diff % 60000) / 1000),
  };
};

const Raffle = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [authOpen, setAuthOpen] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  // Efekt üyenin nesnesine değil **kimliğine** bağlanır: AuthContext arka
  // planda profili tazeleyip yeni bir nesne kurar ve nesneye bağlanan efekt
  // her tazelemede listeyi yeniden çeker (spor kitabı çerçevesindeki tuzağın
  // aynısı).
  const userId = user?.id || '';

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await client.get('/raffles');
      const list = data.items || [];
      setEnabled(data.enabled !== false);
      setItems(list);
      setSelectedId((current) => (
        list.some((item) => item.id === current) ? current : (list[0]?.id || null)
      ));
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Çekilişler yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, userId]);

  const loadDetail = useCallback(async () => {
    if (!selectedId) { setDetail(null); setTickets([]); return; }
    try {
      const { data } = await client.get(`/raffles/${selectedId}`);
      setDetail(data);
    } catch {
      setDetail(null);
    }
    if (!userId) { setTickets([]); return; }
    try {
      const { data } = await client.get(`/raffles/${selectedId}/tickets`, {
        params: { limit: 200 },
      });
      setTickets(data.items || []);
    } catch {
      setTickets([]);
    }
  }, [selectedId, userId]);

  useEffect(() => { loadDetail(); }, [loadDetail]);

  // Geri sayım yalnızca sayılacak bir an varken döner: sonuçlanmış çekilişte
  // saniyede bir render etmek boşuna iştir.
  const countdownTarget = detail?.status === 'active'
    ? detail?.ends_at
    : (detail?.status === 'closed' ? detail?.draw_at : null);

  useEffect(() => {
    if (!countdownTarget) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [countdownTarget]);

  const me = detail?.me;
  const progress = useMemo(() => ticketProgress(me), [me]);
  const remaining = countdownTarget ? splitRemaining(countdownTarget, now) : null;

  if (loading) {
    return (
      <main className="rff-page">
        <div className="rff-skeleton" />
      </main>
    );
  }

  if (error) {
    return (
      <main className="rff-page">
        <div className="rff-empty">
          <Ticket size={38} />
          <h1>Çekiliş açılamadı</h1>
          <p>{error}</p>
        </div>
      </main>
    );
  }

  if (!enabled || items.length === 0) {
    return (
      <main className="rff-page">
        <div className="rff-empty">
          <Ticket size={38} />
          <h1>{enabled ? 'Şu anda yayında çekiliş yok' : 'Çekiliş şu anda kapalı'}</h1>
          <p>
            {enabled
              ? 'Yeni çekilişler duyurulduğunda bu sayfada görünür.'
              : 'Çekiliş bölümü şu anda kapalı. Yeniden açıldığında burada '
                + 'yer alacak.'}
            {' '}
            Güncel kampanyalar için{' '}
            <Link to="/promosyonlar">Bonuslar</Link> sayfasını takip edin.
          </p>
        </div>
      </main>
    );
  }

  const status = detail?.status || 'active';
  const chip = STATUS_CHIPS[status] || STATUS_CHIPS.active;
  const prizes = detail?.prizes || [];
  const winners = detail?.winners || [];
  const shownStubs = tickets.slice(0, STUB_LIMIT);

  return (
    <main className="rff-page">
      {items.length > 1 && (
        <nav className="rff-switch" aria-label="Çekilişler">
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setSelectedId(item.id)}
              className={`rff-switch-item ${item.id === selectedId ? 'is-active' : ''}`}
            >
              <span className="rff-switch-name">{item.title}</span>
              <span className="rff-switch-meta">
                {(STATUS_CHIPS[item.status] || STATUS_CHIPS.active).label}
                {item.prize_pool > 0 ? ` · ${money(item.prize_pool)}` : ''}
              </span>
            </button>
          ))}
        </nav>
      )}

      {detail && (
        <>
          <section className="rff-hero">
            {detail.image_url && (
              <>
                <div className="rff-hero-art">
                  <img src={resolveApiAssetUrl(detail.image_url)} alt="" />
                </div>
                <div className="rff-hero-veil" />
              </>
            )}

            <div className="rff-hero-body">
              <div className="rff-hero-main">
                <div className="rff-chips">
                  <span className={`rff-chip ${chip.tone}`}>
                    {status === 'active' && <span className="rff-chip-dot" />}
                    {chip.label}
                  </span>
                  <span className="rff-chip">
                    <Ticket size={11} /> {money(detail.ticket_amount)} = 1 bilet
                  </span>
                  {detail.balance_counts && (
                    <span className="rff-chip is-gold">
                      <Wallet size={11} /> Bakiyeniz de sayılır
                    </span>
                  )}
                  {detail.winner_slots > 0 && (
                    <span className="rff-chip">
                      <Users size={11} /> {compact(detail.winner_slots)} kişi kazanacak
                    </span>
                  )}
                </div>

                <h1 className="rff-title gold-text">{detail.title}</h1>
                {detail.description && <p className="rff-lede">{detail.description}</p>}

                {detail.prize_pool > 0 && (
                  <div className="rff-pool">
                    <span className="rff-pool-value gold-text">
                      {money(detail.prize_pool)}
                    </span>
                    <span className="rff-pool-label">toplam para ödülü</span>
                  </div>
                )}

                {remaining ? (
                  <div className="rff-countdown">
                    <span className="rff-count-box">
                      <span className="rff-count-value">{remaining.days}</span>
                      <span className="rff-count-label">Gün</span>
                    </span>
                    <span className="rff-count-box">
                      <span className="rff-count-value">
                        {String(remaining.hours).padStart(2, '0')}
                      </span>
                      <span className="rff-count-label">Saat</span>
                    </span>
                    <span className="rff-count-box">
                      <span className="rff-count-value">
                        {String(remaining.minutes).padStart(2, '0')}
                      </span>
                      <span className="rff-count-label">Dakika</span>
                    </span>
                    <span className="rff-count-box">
                      <span className="rff-count-value">
                        {String(remaining.seconds).padStart(2, '0')}
                      </span>
                      <span className="rff-count-label">Saniye</span>
                    </span>
                    <span className="rff-count-note">
                      {status === 'active'
                        ? 'bilet kazanmak için kaldı'
                        : 'kuraya kaldı'}
                    </span>
                  </div>
                ) : (
                  <div className="rff-countdown">
                    <span className="rff-count-note">
                      {status === 'drawn'
                        ? `Kura ${formatDate(detail.drawn_at)} tarihinde çekildi.`
                        : 'Bilet kazanma süresi doldu, kura bekleniyor.'}
                    </span>
                  </div>
                )}
              </div>

              <aside className="rff-ticket">
                <span className="rff-ticket-label">Biletleriniz</span>
                <div className="rff-ticket-value gold-text">
                  {userId ? compact(me?.tickets ?? 0) : '—'}
                </div>
                <span className="rff-ticket-unit">
                  {userId
                    ? (me?.basis_source === 'balance'
                      ? `${money(me?.balance ?? 0)} bakiyenizle kazanıldı`
                      : `${money(me?.deposited ?? 0)} yatırımla kazanıldı`)
                    : 'Biletlerinizi görmek için giriş yapın'}
                </span>

                {userId && status === 'active' && (
                  <>
                    <div className="rff-progress">
                      <i style={{ width: `${progress}%` }} />
                    </div>
                    <p className="rff-progress-note">
                      {me?.next_ticket_in > 0 ? (
                        <>
                          Bir sonraki bilet için{' '}
                          <b>{money(me.next_ticket_in)}</b> daha yatırın
                        </>
                      ) : (
                        'Her yatırımınız anında bilete dönüşür.'
                      )}
                    </p>
                    {/* Bakiyesi olduğu hâlde bileti olmayan oyuncunun ilk
                        sorusu "param var, neden bilet yok" oluyor: ölçü
                        bakiye değil çekiliş dönemindeki onaylı yatırımdır
                        (bonus ve panelden eklenen bakiye sayılmaz). Sebep
                        ekranda yazmazsa şikâyet canlı desteğe gider. */}
                    {(me?.basis ?? 0) <= 0 && (
                      <p className="rff-progress-note rff-progress-hint">
                        {detail.balance_counts ? (
                          <>
                            Bilet için hesabınızda en az{' '}
                            <b>{money(detail.ticket_amount)}</b> bakiye ya da
                            çekiliş döneminde o kadar <b>onaylı yatırım</b>
                            {' '}gerekir.
                          </>
                        ) : (
                          <>
                            Hesabınızdaki bakiye bilet üretmez: yalnızca çekiliş
                            dönemindeki <b>onaylı para yatırma</b> işlemleriniz
                            sayılır.
                          </>
                        )}
                      </p>
                    )}
                  </>
                )}

                {userId ? (
                  status === 'active' ? (
                    <button
                      type="button"
                      onClick={() => navigate('/hesap/para-yatir')}
                      className="gold-btn rff-cta"
                    >
                      <Wallet size={16} /> Para Yatır, Bilet Kazan
                    </button>
                  ) : null
                ) : (
                  <button
                    type="button"
                    onClick={() => setAuthOpen(true)}
                    className="gold-btn rff-cta"
                  >
                    <LogIn size={16} /> Giriş Yap
                  </button>
                )}

                <p className="rff-ticket-foot">
                  {me?.max_tickets_per_user > 0
                    ? `Üye başına en çok ${compact(me.max_tickets_per_user)} bilet.`
                    : 'Bilet sayısında üst sınır yok.'}
                  {me?.manual_tickets > 0
                    && ` ${compact(me.manual_tickets)} bilet tarafımızca tanımlandı.`}
                </p>
              </aside>
            </div>
          </section>

          <section className="rff-section">
            <div className="rff-section-head">
              <Dices size={16} />
              <h2>Nasıl katılırım?</h2>
            </div>
            <div className="rff-steps">
              <article className="rff-step">
                <span className="rff-step-no">1</span>
                <h3>Para yatırın</h3>
                <p>
                  Çekiliş döneminde yaptığınız <b>onaylı</b> yatırımlar sayılır.
                  Ayrıca bir işlem yapmanıza gerek yok.
                </p>
              </article>
              <article className="rff-step">
                <span className="rff-step-no">2</span>
                <h3>Biletiniz otomatik gelsin</h3>
                <p>
                  Her <b>{money(detail.ticket_amount)}</b> için 1 bilet
                  kazanırsınız. Artan tutar kaybolmaz, bir sonraki yatırımınıza
                  eklenir.
                  {detail.balance_counts && (
                    <>
                      {' '}Bu çekilişte <b>hesabınızdaki bakiye</b> de sayılır:
                      yatırımınızla bakiyenizden hangisi yüksekse o geçerlidir.
                    </>
                  )}
                </p>
              </article>
              <article className="rff-step">
                <span className="rff-step-no">3</span>
                <h3>Kurayı bekleyin</h3>
                <p>
                  Kazanan bilet rastgele seçilir. <b>Para ödülleri</b> kura
                  anında hesabınıza geçer, diğer ödüller için sizinle iletişime
                  geçilir.
                </p>
              </article>
            </div>
          </section>

          {prizes.length > 0 && (
            <section className="rff-section">
              <div className="rff-section-head">
                <Gift size={16} />
                <h2>Ödüller</h2>
                <span className="rff-section-meta">
                  {compact(detail.winner_slots)} kişi kazanacak
                </span>
              </div>
              <div className="rff-prizes">
                {prizes.map((prize, index) => {
                  const Icon = PRIZE_ICONS[prize.reward_type] || Coins;
                  return (
                    <div className="rff-prize" key={prize.id}>
                      <span className="rff-prize-rank">{index + 1}</span>
                      <div className="rff-prize-text">
                        <div className="rff-prize-title">{prize.title}</div>
                        <div className="rff-prize-sub">
                          {prize.winner_count > 1
                            ? `${prize.winner_count} kişiye`
                            : 'Tek kişiye'}
                          {prize.reward_type === 'bonus' && ' · Bonus (çevrim şartlı)'}
                          {prize.reward_type === 'cash' && ' · Ana bakiyeye'}
                        </div>
                      </div>
                      <span className={`rff-prize-value ${
                        prize.reward_type === 'custom' ? 'is-custom' : ''}`}
                      >
                        <Icon size={14} />
                        {prize.reward_type === 'custom' ? 'Sürpriz ödül' : money(prize.amount)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {userId && tickets.length > 0 && (
            <section className="rff-section">
              <div className="rff-section-head">
                <Ticket size={16} />
                <h2>Bilet numaralarınız</h2>
                <span className="rff-section-meta">{compact(me?.tickets ?? tickets.length)} bilet</span>
              </div>
              <div className="rff-stubs">
                {shownStubs.map((ticket) => (
                  <span
                    key={ticket.ticket_no}
                    className={`rff-stub ${ticket.source === 'manual' ? 'is-manual' : ''}`}
                    title={ticket.source === 'manual' ? 'Tarafımızca tanımlandı' : 'Yatırımla kazanıldı'}
                  >
                    #{ticket.ticket_no}
                  </span>
                ))}
                {tickets.length > shownStubs.length && (
                  <span className="rff-stub-more">
                    +{compact(tickets.length - shownStubs.length)} bilet daha
                  </span>
                )}
              </div>
            </section>
          )}

          {winners.length > 0 && (
            <section className="rff-section">
              <div className="rff-section-head">
                <Trophy size={16} />
                <h2>Kazananlar</h2>
                <span className="rff-section-meta">{compact(winners.length)} kişi</span>
              </div>
              <div className="rff-winners">
                {winners.map((winner) => (
                  <div
                    key={`${winner.ticket_no}-${winner.prize_title}`}
                    className={`rff-winner ${winner.mine ? 'is-mine' : ''}`}
                  >
                    <span className="rff-winner-no">#{winner.ticket_no}</span>
                    <div className="rff-winner-text">
                      <div className="rff-winner-name">
                        {winner.username}
                        {winner.mine && <CheckCircle2 size={13} />}
                      </div>
                      <div className="rff-winner-prize">{winner.prize_title}</div>
                    </div>
                    {winner.reward_type !== 'custom' && (
                      <span className="rff-winner-amount">{money(winner.amount)}</span>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="rff-section">
            <div className="rff-section-head">
              <ShieldCheck size={16} />
              <h2>Kurallar</h2>
            </div>
            <div className="rff-rules">
              <p className="rff-rule">
                <CalendarClock size={14} />
                <span>
                  Bilet kazandıran dönem: <b>{formatDate(detail.starts_at)}</b> –{' '}
                  <b>{formatDate(detail.ends_at)}</b>. Dönem dışındaki yatırımlar
                  sayılmaz.
                </span>
              </p>
              {detail.draw_at && (
                <p className="rff-rule">
                  <Dices size={14} />
                  <span>Kura tarihi: <b>{formatDate(detail.draw_at)}</b>.</span>
                </p>
              )}
              <p className="rff-rule">
                <Ticket size={14} />
                <span>
                  {detail.balance_counts ? (
                    <>
                      Bilet, çekiliş dönemindeki <b>onaylanmış yatırımınız</b>
                      {' '}ile <b>hesap bakiyeniz</b>den hangisi yüksekse ondan
                      hesaplanır; ikisi toplanmaz. Bakiyeniz sonradan düşse de
                      kazanılmış biletler geri alınmaz.
                    </>
                  ) : (
                    <>
                      Yalnızca <b>onaylanmış para yatırma</b> işlemleri sayılır;
                      bonuslar, hesap bakiyesi ve bekleyen talepler bilet
                      kazandırmaz.
                    </>
                  )}
                </span>
              </p>
              <p className="rff-rule">
                <Trophy size={14} />
                <span>
                  Kazanan bilet rastgele seçilir ve kura yalnızca bir kez
                  çekilir. Kazanan numaralar bu sayfada yayınlanır.
                </span>
              </p>
            </div>
          </section>
        </>
      )}

      <AuthModal
        open={authOpen}
        onClose={() => setAuthOpen(false)}
        defaultTab="login"
      />
    </main>
  );
};

export default Raffle;
